// src/api.js
const envBase = process.env.REACT_APP_API_BASE_URL;
function normalizeBaseUrl(v) {
	if (!v || v === 'undefined' || v === 'null') return null;
	const s = String(v).trim();
	// Handle common malformed values
	if (/^[-:]?\d{2,5}$/.test(s)) {
		const port = s.replace(/[^0-9]/g, '');
		return `http://localhost:${port}`;
	}
	if (/^localhost:\d{2,5}$/.test(s)) {
		return `http://${s}`;
	}
	if (/^:\d{2,5}$/.test(s)) {
		return `http://localhost${s}`;
	}
	if (!/^https?:\/\//i.test(s)) {
		// Assume host[:port] without protocol
		return `http://${s}`;
	}
	return s;
}

export const API_BASE_URL = normalizeBaseUrl(envBase) || 'http://localhost:4000';

const inflightTeamLoadByUrl = new Map();

function getToken() {
	try { return localStorage.getItem('token'); } catch { return null; }
}

function handleAuthRedirect(res) {
	if (res && res.status === 401) {
		try { localStorage.removeItem('token'); } catch {}
		window.location.href = '/';
		return true;
	}
	return false;
}

async function fetchWithAuth(url, options = {}) {
	const token = getToken();
	if (!token) {
		const err = new Error('Not authenticated');
		err.status = 401;
		throw err;
	}

	const { redirectOn401 = true, headers, ...rest } = options || {};
	const res = await fetch(url, {
		...rest,
		headers: {
			...(headers || {}),
			Authorization: `Bearer ${token}`,
		},
	});

	if (redirectOn401 && handleAuthRedirect(res)) {
		const err = new Error('Not authenticated');
		err.status = 401;
		throw err;
	}

	if (!res.ok) {
		let fallback = `Request failed (${res.status})`;
		if (res.status === 403) fallback = 'Forbidden (403)';
		if (res.status === 401) fallback = 'Not authenticated';
		const msg = await parseJsonErrorMessage(res, fallback);
		const err = new Error(msg);
		err.status = res.status;
		throw err;
	}

	return res;
}

export async function fetchPeopleOverview({ period, projectId, start, end } = {}) {
	const qs = new URLSearchParams();
	if (period) qs.set('period', String(period));
	if (projectId != null && String(projectId).trim() !== '' && String(projectId) !== 'all') {
		qs.set('projectId', String(projectId));
	}
	if (start) qs.set('start', String(start));
	if (end) qs.set('end', String(end));
	const url = `${API_BASE_URL}/api/people/overview${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetchWithAuth(url);
	return res.json();
}

export async function fetchPersonIntelligence(personId, { period, projectId, start, end } = {}) {
	if (!personId) throw new Error('Missing personId');
	const qs = new URLSearchParams();
	if (period) qs.set('period', String(period));
	if (projectId != null && String(projectId).trim() !== '' && String(projectId) !== 'all') {
		qs.set('projectId', String(projectId));
	}
	if (start) qs.set('start', String(start));
	if (end) qs.set('end', String(end));
	const url = `${API_BASE_URL}/api/people/${encodeURIComponent(String(personId))}/intelligence${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetchWithAuth(url);
	return res.json();
}

export async function fetchActivePresence({ minutes = 5 } = {}) {
	const m = Number(minutes);
	const clamped = Number.isFinite(m) ? Math.min(240, Math.max(1, Math.floor(m))) : 5;
	const res = await fetchWithAuth(`${API_BASE_URL}/api/presence/active?minutes=${encodeURIComponent(String(clamped))}`);
	return res.json();
}

export async function addTimeEntry(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');

	const res = await fetch(`${API_BASE_URL}/api/time-entries`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify(payload),
	});

	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to add time entry');
	}

	return res.json();
}

// Upsert helper: set actual hours for a given assignment on a given day.
// Backend implements: POST /api/time-entries/day-task
// Body: { assignment_id, entry_date: 'YYYY-MM-DD', hours: number }
export async function upsertTaskHoursForDay({ assignmentId, dateISO, hours, description } = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const assignment_id = assignmentId;
	const entry_date = String(dateISO || '').slice(0, 10);
	const hoursNum = Number(hours);
	const note = (typeof description === 'string') ? description : (description == null ? '' : String(description));
	if (!assignment_id) throw new Error('Missing assignmentId');
	if (!/^\d{4}-\d{2}-\d{2}$/.test(entry_date)) throw new Error('Invalid dateISO (expected YYYY-MM-DD)');
	if (!Number.isFinite(hoursNum) || hoursNum < 0 || hoursNum > 24) throw new Error('hours must be a number between 0 and 24');

	const tryRes = await fetch(`${API_BASE_URL}/api/time-entries/day-task`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify({ assignment_id, entry_date, hours: hoursNum, description: note }),
	});
	if (handleAuthRedirect(tryRes)) return null;

	// Fallback: if the endpoint isn't implemented yet, we can only safely INCREASE
	// logged time by creating a delta minutes entry. Decreases/clears require backend support.
	if (tryRes.status === 404) {
		if (hoursNum <= 0) {
			const err = new Error('Clearing or reducing hours requires backend support.');
			err.status = 404;
			throw err;
		}
		const deltaMinutes = Math.round(hoursNum * 60);
		const fallbackDescription = String(note || '').trim() || 'Actual hours (fallback)';
		const res = await fetch(`${API_BASE_URL}/api/time-entries`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'Authorization': `Bearer ${token}`,
			},
			body: JSON.stringify({ assignment_id, entry_date, minutes: deltaMinutes, description: fallbackDescription }),
		});
		if (handleAuthRedirect(res)) return null;
		if (!res.ok) {
			let message = `Request failed (${res.status})`;
			try {
				const data = await res.json();
				if (data && data.error) message = data.error;
			} catch (_) {
				try { message = await res.text(); } catch {}
			}
			const err = new Error(message || 'Failed to log task hours');
			err.status = res.status;
			throw err;
		}
		return res.json();
	}

	if (!tryRes.ok) {
		let message = `Request failed (${tryRes.status})`;
		try {
			const data = await tryRes.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await tryRes.text(); } catch {}
		}
		const err = new Error(message || 'Failed to log task hours');
		err.status = tryRes.status;
		throw err;
	}
	return tryRes.json();
}

// Backwards-compatible alias: previous Step 1 helper name.
export async function logTaskHoursForDay({ assignmentId, dateISO, hours } = {}) {
	return upsertTaskHoursForDay({ assignmentId, dateISO, hours });
}

// Placeholder helper: update an "other" time entry.
// If project/job/task/date/manager changes, we emulate an update via create+delete.
export async function updateOtherLog({ id, clientId, projectId, category, jobId, taskId, hours, note, dateISO, managerId } = {}) {
	const entryId = id;
	if (!entryId) throw new Error('Missing id');

	const entry_date = String(dateISO || '').slice(0, 10);
	if (!/^\d{4}-\d{2}-\d{2}$/.test(entry_date)) throw new Error('Invalid dateISO (expected YYYY-MM-DD)');

	// Accept either jobId/category naming
	const project_id = projectId || clientId || null;
	const job_id = jobId || category || null;
	const task_id = taskId || null;
	const for_manager_user_id = managerId || null;

	const minutes = Math.round(Number(hours || 0) * 60);
	if (!Number.isInteger(minutes) || minutes <= 0 || minutes > 1440) throw new Error('hours must be > 0 and <= 24');
	if (minutes % 15 !== 0) throw new Error('Minutes must be in 15-minute increments');

	const existing = await fetchTimeEntry(entryId);
	if (!existing) throw new Error('Time entry not found');
	if (!existing.is_other) throw new Error('Not an other log');

	const oldProject = existing.project_id || null;
	const oldJob = existing.job_id || null;
	const oldTask = existing.task_id || null;
	const oldDate = String(existing.entry_date || '').slice(0, 10);
	const oldMgr = existing.for_manager_user_id || null;

	const needsMove = String(oldDate) !== String(entry_date)
		|| String(oldProject || '') !== String(project_id || '')
		|| String(oldJob || '') !== String(job_id || '')
		|| String(oldTask || '') !== String(task_id || '')
		|| String(oldMgr || '') !== String(for_manager_user_id || '');

	if (needsMove) {
		const created = await addOtherTimeEntry({
			entry_date,
			minutes,
			project_id,
			job_id,
			task_id,
			description: typeof note === 'string' ? note : (note == null ? '' : String(note)),
			for_manager_user_id,
		});
		await deleteTimeEntry(entryId);
		return { ok: true, replaced: true, id: created?.id || null, entry: created || null };
	}

	await updateTimeEntry(entryId, {
		minutes,
		description: typeof note === 'string' ? note : (note == null ? null : String(note)),
	});
	return { ok: true, replaced: false, id: entryId };
}

// Placeholder helper: delete an "other" time entry.
export async function deleteOtherLog({ id } = {}) {
	if (!id) throw new Error('Missing id');
	return deleteTimeEntry(id);
}

// Placeholder helper: mark a task instance as done for a specific day.
// Backend should implement: POST /api/tasks/day-done
// Body: { assignment_id, entry_date: 'YYYY-MM-DD' }
export async function markTaskDoneForDay({ assignmentId, dateISO } = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const assignment_id = assignmentId;
	const entry_date = String(dateISO || '').slice(0, 10);
	if (!assignment_id) throw new Error('Missing assignmentId');
	if (!/^\d{4}-\d{2}-\d{2}$/.test(entry_date)) throw new Error('Invalid dateISO (expected YYYY-MM-DD)');

	const res = await fetch(`${API_BASE_URL}/api/tasks/day-done`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		body: JSON.stringify({ assignment_id, entry_date }),
	});
	if (handleAuthRedirect(res)) return null;
	if (res.status === 404) {
		// Frontend-only step: allow UI to work even if backend route isn't implemented yet.
		return { ok: true, placeholder: true };
	}
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try { const data = await res.json(); if (data?.error) message = data.error; } catch { try { message = await res.text(); } catch {} }
		const err = new Error(message || 'Failed to mark done for day');
		err.status = res.status;
		throw err;
	}
	return res.json();
}

// Placeholder helper: mark an entire assignment as completed.
// Backend should implement: POST /api/tasks/assignment-done
// Body: { assignment_id }
export async function markAssignmentCompleted(arg = {}) {
	const assignmentId = (arg && typeof arg === 'object') ? arg.assignmentId : arg;
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const assignment_id = assignmentId;
	if (!assignment_id) throw new Error('Missing assignmentId');

	const res = await fetch(`${API_BASE_URL}/api/tasks/assignment-done`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		body: JSON.stringify({ assignment_id }),
	});
	if (handleAuthRedirect(res)) return null;
	if (res.status === 404) {
		// Frontend-only step: allow UI to work even if backend route isn't implemented yet.
		return { ok: true, placeholder: true };
	}
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try { const data = await res.json(); if (data?.error) message = data.error; } catch { try { message = await res.text(); } catch {} }
		const err = new Error(message || 'Failed to mark assignment completed');
		err.status = res.status;
		throw err;
	}
	return res.json();
}

// Placeholder helper: accept a task/assignment.
// Preferred backend: POST /api/tasks/accept
// Body: { assignment_id }
// Falls back to legacy employee route if the new route is not implemented.
export async function acceptTask(arg = {}) {
	const assignmentId = (arg && typeof arg === 'object') ? arg.assignmentId : arg;
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const assignment_id = assignmentId;
	if (!assignment_id) throw new Error('Missing assignmentId');

	const res = await fetch(`${API_BASE_URL}/api/tasks/accept`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		body: JSON.stringify({ assignment_id }),
	});
	if (handleAuthRedirect(res)) return null;
	if (res.status === 404) {
		// Backward compatible path (older API)
		return acceptMyAssignment(assignment_id);
	}
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try { const data = await res.json(); if (data?.error) message = data.error; } catch { try { message = await res.text(); } catch {} }
		const err = new Error(message || 'Failed to accept task');
		err.status = res.status;
		throw err;
	}
	return res.json();
}

export async function deleteTimeEntry(id) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');

	const res = await fetch(`${API_BASE_URL}/api/time-entries/${encodeURIComponent(String(id))}`, {
		method: 'DELETE',
		headers: { 'Authorization': `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to delete time entry');
	}
	return res.json();
}

export async function fetchTimeEntry(id) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');

	const res = await fetch(`${API_BASE_URL}/api/time-entries/${encodeURIComponent(String(id))}`, {
		headers: { 'Authorization': `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to load time entry');
	}
	return res.json();
}

export async function updateTimeEntry(id, payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');

	const res = await fetch(`${API_BASE_URL}/api/time-entries/${encodeURIComponent(String(id))}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
		body: JSON.stringify(payload || {}),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to update time entry');
	}
	return res.json();
}

export async function fetchPlanningLookups(options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/lookups/planning`, {
		headers: { Authorization: `Bearer ${token}` },
	});
	if (redirectOn401 && handleAuthRedirect(res)) return null;
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) throw new Error('Failed to load lookups');
	return res.json();
}

// Admin Catalog (Andrea only)
export async function fetchAdminCatalog() {
	const token = getToken();
	const tryFetch = async (url) => {
		const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
		if (res.ok) return res.json();
		throw new Error(`HTTP ${res.status}`);
	};
	try {
		return await tryFetch(`${API_BASE_URL}/api/admin/catalog`);
	} catch (_) {
		// Fallback to manager namespace if admin route not found
		return await tryFetch(`${API_BASE_URL}/api/manager/catalog`);
	}
}

export async function createJob({ name, catalog_group }) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/jobs`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ name, catalog_group })
	});
	if (!res.ok) throw new Error('Failed to create job');
	return res.json();
}

export async function updateJob(id, payload) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/jobs/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload)
	});
	if (!res.ok) throw new Error('Failed to update job');
	return res.json();
}

export async function deleteJob(id) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/jobs/${id}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (res.status === 204) return { deleted: true };
	if (!res.ok) throw new Error('Failed to delete/archive job');
	return res.json();
}

export async function createCatalogTask(jobId, { name }) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/jobs/${jobId}/tasks`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ name })
	});
	if (!res.ok) throw new Error('Failed to create task');
	return res.json();
}

export async function updateCatalogTask(id, payload) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/tasks/${id}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload)
	});
	if (!res.ok) throw new Error('Failed to update task');
	return res.json();
}

export async function deleteCatalogTask(id) {
	const token = getToken();
	const res = await fetch(`${API_BASE_URL}/api/admin/catalog/tasks/${id}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` }
	});
	if (res.status === 204) return { deleted: true };
	if (!res.ok) throw new Error('Failed to delete/archive task');
	return res.json();
}

export async function addOtherTimeEntry(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/time-entries/other`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify(payload),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to add time entry');
	}
	return res.json();
}

export async function fetchWeeklyReport(start) {
	throw new Error('Feature in migration');
}

export async function fetchWeeklyByCategory(start) {
	throw new Error('Feature in migration');
}

export async function fetchMyAssignments(params = {}) {
  const token = getToken();
  if (!token) throw new Error('Not authenticated');
  const qs = new URLSearchParams(
	Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
  ).toString();
  const url = `${API_BASE_URL}/api/me/assignments${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  if (handleAuthRedirect(res)) return []; // redirected
  if (!res.ok) throw new Error('Feature in migration');
  return res.json();
}

// Employee My Work (week/month views)
// - { view: 'week', offset: 0 } -> this week
// - { view: 'week', offset: 1 } -> next week
// - { view: 'month', month: 'YYYY-MM' } -> given month
// - { view: 'month', date: 'YYYY-MM-DD' } -> month containing anchor date
export async function fetchMyWork(params = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const qs = new URLSearchParams(
		Object.fromEntries(Object.entries(params).filter(([_, v]) => v != null && v !== ''))
	).toString();
	const url = `${API_BASE_URL}/api/me/my-work${qs ? `?${qs}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to fetch my work';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Employee/junior: accept an assignment (must be assignee)
export async function acceptMyAssignment(assignmentId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/employee/assignments/${assignmentId}/accept`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({}),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to accept assignment';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Employee/junior: set progress for an assignment (must be assignee)
export async function setMyAssignmentProgress(assignmentId, progress) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me/assignments/${assignmentId}/progress`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify({ progress }),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to update progress';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function fetchMyOtherEntries(limit = 10) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const qs = new URLSearchParams({ limit: String(limit) }).toString();
	const url = `${API_BASE_URL}/api/me/other-entries${qs ? `?${qs}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return [];
	if (!res.ok) throw new Error('Failed to fetch other entries');
	return res.json();
}

export async function fetchMyWorkSummary(range = 'this_week') {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	let url = `${API_BASE_URL}/api/me/my-work-summary`;
	// Backward compatible:
	// - fetchMyWorkSummary('this_week')
	// - fetchMyWorkSummary({ from: 'YYYY-MM-DD', to: 'YYYY-MM-DD' })
	if (range && typeof range === 'object') {
		const from = range.from;
		const to = range.to;
		if (from && to) {
			const qs = new URLSearchParams({ from: String(from), to: String(to) }).toString();
			url += `?${qs}`;
		} else {
			url += `?range=this_week`;
		}
	} else {
		url += `?range=${encodeURIComponent(range)}`;
	}
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch summary');
	return res.json();
}

// Employee: project breakdown for a selected period
// Returns: [{ project_id, project_name, planned_minutes, logged_minutes }]
export async function fetchMyProjectBreakdown({ startDate, endDate } = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const qs = new URLSearchParams();
	if (startDate) qs.set('startDate', String(startDate));
	if (endDate) qs.set('endDate', String(endDate));
	const url = `${API_BASE_URL}/api/me/my-project-breakdown${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return [];
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to fetch project breakdown'));
	const rows = await res.json();
	return Array.isArray(rows) ? rows : [];
}

export async function fetchProjectEmployeesByManager(options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { onlyMine = false, includeInactive = false } = options || {};
	const qs = new URLSearchParams();
	if (onlyMine) qs.set('only_mine', '1');
	if (includeInactive) qs.set('include_inactive', '1');
	const url = `${API_BASE_URL}/api/manager/project-employees${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to load project employees');
	return res.json();
}

export async function startAssignment(id) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/assignments/${id}/start`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to start assignment');
	return res.json();
}

export async function completeAssignment(id) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/employee/assignments/${id}/done`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to mark task done';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		const err = new Error(msg);
		err.status = res.status;
		throw err;
	}
	return res.json();
}

export async function createTask(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/tasks`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			'Authorization': `Bearer ${token}`,
		},
		body: JSON.stringify(payload),
	});
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to create task');
	}
	return res.json();
}

// Planned task (assignment) delete
// DELETE /api/tasks/:taskId
// - 200 { ok: true }
// - 409 { error: "TASK_HAS_LOGGED_TIME" }
export async function deletePlannedTask(taskId) {
	const res = await fetchWithAuth(`${API_BASE_URL}/api/tasks/${encodeURIComponent(String(taskId))}`, {
		method: 'DELETE',
	});
	return res.json();
}

// Planned task (assignment) day removal
// DELETE /api/tasks/:taskId/days/:ymd
// - Employee: removes for self
// - Manager/admin: must pass userId (assignee) as query param
// - 409 { error: "DAY_HAS_LOGGED_TIME" }
export async function removePlannedTaskDay(taskId, ymd, options = {}) {
	const { userId = null } = options || {};
	const qs = userId ? `?user_id=${encodeURIComponent(String(userId))}` : '';
	const res = await fetchWithAuth(
		`${API_BASE_URL}/api/tasks/${encodeURIComponent(String(taskId))}/days/${encodeURIComponent(String(ymd))}${qs}`,
		{ method: 'DELETE' }
	);
	return res.json();
}

// Control Panel summary (manager-like)
// GET /api/dashboard/control
export async function fetchControlPanelSummary(period = 'today', options = {}) {
	// Back-compat: previous signature was fetchControlPanelSummary(options)
	if (period && typeof period === 'object') {
		options = period;
		period = 'today';
	}

	const { start = null, end = null, from = null, to = null, range = null, ...fetchOptions } = options || {};
	const startYmd = (range && typeof range === 'object') ? (range.start ?? range.from ?? null) : (start ?? from ?? null);
	const endYmd = (range && typeof range === 'object') ? (range.end ?? range.to ?? null) : (end ?? to ?? null);

	try {
		const qs = new URLSearchParams();
		if (period) qs.set('period', String(period));
		if (startYmd) qs.set('start', String(startYmd));
		if (endYmd) qs.set('end', String(endYmd));
		const url = `${API_BASE_URL}/api/dashboard/control${qs.toString() ? `?${qs.toString()}` : ''}`;
		const res = await fetchWithAuth(url, fetchOptions);
		return res.json();
	} catch (err) {
		const message = String(err?.message || '').trim() || 'Failed to fetch control panel summary';
		const e = new Error(message);
		if (err?.status) e.status = err.status;
		throw e;
	}
}

export async function fetchTeamLoad(range = 'this_week', projectId = null, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true, from = null, to = null, projectIds = null, mode = null } = options || {};
	const params = new URLSearchParams();
	params.set('range', range);
	if (from && to) {
		params.set('from', String(from));
		params.set('to', String(to));
	}
	if (mode != null && String(mode).trim() !== '') {
		params.set('mode', String(mode));
	}
	if (projectIds != null) {
		if (projectIds === 'all') {
			params.set('project_ids', 'all');
		} else if (Array.isArray(projectIds)) {
			params.set('project_ids', projectIds.map(Number).filter(n => Number.isFinite(n) && n > 0).join(','));
		} else {
			params.set('project_ids', String(projectIds));
		}
	} else if (projectId != null && projectId !== '') {
		params.set('project_id', String(projectId));
	}
	const url = `${API_BASE_URL}/api/manager/team-load?${params.toString()}`;
		if (process.env.NODE_ENV !== 'production' && params.has('project_ids')) {
			console.log('[fetchTeamLoad bulk] GET', url);
		}
	if (inflightTeamLoadByUrl.has(url)) return inflightTeamLoadByUrl.get(url);
	const p = (async () => {
		const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
		if (redirectOn401 && handleAuthRedirect(res)) return null;
		if (res.status === 401) throw new Error('Not authenticated');
		if (!res.ok) throw new Error('Failed to fetch team load');
		return res.json();
	})();
	inflightTeamLoadByUrl.set(url, p);
	try {
		return await p;
	} finally {
		inflightTeamLoadByUrl.delete(url);
	}
}

export async function fetchUserWorkload(userId, from, to, projectId = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams();
	params.set('from', from);
	params.set('to', to);
	if (projectId != null && projectId !== '') params.set('project_id', String(projectId));
	const url = `${API_BASE_URL}/api/manager/users/${userId}/workload?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch workload');
	return res.json();
}

export async function fetchPersonWeeklyWorkload(personId, weekStart, projectId = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams();
	params.set('weekStart', String(weekStart));
	if (projectId != null && projectId !== '') params.set('project_id', String(projectId));
	const url = `${API_BASE_URL}/api/manager/people/${encodeURIComponent(String(personId))}/workload?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch weekly workload');
	return res.json();
}

export async function fetchUserProjectBreakdown(userId, projectId, from, to) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	if (projectId == null || projectId === '') throw new Error('projectId is required');
	const params = new URLSearchParams();
	params.set('from', from);
	params.set('to', to);
	params.set('project_id', String(projectId));
	const url = `${API_BASE_URL}/api/manager/users/${userId}/project-breakdown?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch project breakdown');
	return res.json();
}

export async function fetchManagerClients(options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { includeArchived = false } = options || {};
	const qs = new URLSearchParams();
	if (includeArchived) qs.set('include_archived', 'true');
	const url = `${API_BASE_URL}/api/manager/clients${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch clients');
	return res.json();
}

export async function archiveManagerClient(clientId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/clients/${clientId}/archive`, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to archive client'));
	return res.json();
}

export async function unarchiveManagerClient(clientId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/clients/${clientId}/unarchive`, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to unarchive client'));
	return res.json();
}

export async function deleteAdminClient(clientId, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { force = false } = options || {};
	const qs = new URLSearchParams();
	if (force) qs.set('force', 'true');
	const url = `${API_BASE_URL}/api/admin/clients/${clientId}${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to delete client'));
	return res.json();
}

export async function createManagerClient(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/clients`, {
		method: 'POST',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify(payload || {}),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to create client'));
	return res.json();
}

export async function updateManagerClientColor(clientId, color) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/clients/${clientId}`, {
		method: 'PATCH',
		headers: {
			'Content-Type': 'application/json',
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ color }),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let message = `Request failed (${res.status})`;
		try {
			const data = await res.json();
			if (data && data.error) message = data.error;
		} catch (_) {
			try { message = await res.text(); } catch {}
		}
		throw new Error(message || 'Failed to update client');
	}
	return res.json();
}

export async function fetchManagerProjects(options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { includeArchived = false } = options || {};
	const qs = new URLSearchParams();
	if (includeArchived) qs.set('include_archived', 'true');
	const url = `${API_BASE_URL}/api/manager/projects${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch projects');
	return res.json();
}

export async function updateManagerProject(projectId, payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/projects/${projectId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload || {}),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to update project'));
	return res.json();
}

export async function deleteManagerProject(projectId, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { force = false } = options || {};
	const qs = new URLSearchParams();
	if (force) qs.set('force', 'true');
	const url = `${API_BASE_URL}/api/manager/projects/${projectId}${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to delete project'));
	return res.json();
}

export async function deleteManagerClient(clientId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/manager/clients/${clientId}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to delete client'));
	return res.json();
}

async function parseJsonErrorMessage(res, fallback) {
	let message = fallback || `Request failed (${res?.status})`;
	try {
		const data = await res.json();
		if (data?.error) message = data.error;
		else if (data?.detail) message = data.detail;
	} catch (_) {
		try { message = await res.text(); } catch {}
	}
	return message || fallback || 'Request failed';
}

// --- Vacation / Time off (manager/admin) ---
export async function fetchVacationEntries({ from, to } = {}) {
	const qs = new URLSearchParams();
	if (from) qs.set('from', String(from));
	if (to) qs.set('to', String(to));
	const url = `${API_BASE_URL}/api/vacation${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetchWithAuth(url);
	return res.json();
}

// Vacation warnings: only returned when there's a real conflict (planned task days overlap time off days).
// POST /api/vacation/warnings
export async function fetchVacationWarnings(payload) {
	const res = await fetchWithAuth(`${API_BASE_URL}/api/vacation/warnings`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload || {}),
	});
	return res.json();
}

export async function createVacationEntry(payload) {
	const res = await fetchWithAuth(`${API_BASE_URL}/api/vacation`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload || {}),
	});
	return res.json();
}

export async function updateVacationEntry(id, payload) {
	const res = await fetchWithAuth(`${API_BASE_URL}/api/vacation/${encodeURIComponent(String(id))}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify(payload || {}),
	});
	return res.json();
}

export async function deleteVacationEntry(id) {
	const res = await fetchWithAuth(`${API_BASE_URL}/api/vacation/${encodeURIComponent(String(id))}`, {
		method: 'DELETE',
	});
	return res.json();
}

// --- Admin People management (scaffold; backend added next step) ---
export async function fetchAdminUsers() {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	let url = `${API_BASE_URL}/api/admin/users`;
	const opts = arguments.length > 0 ? arguments[0] : {};
	if (opts && typeof opts === 'object') {
		const qs = new URLSearchParams();
		if (opts.includeDisabled) qs.set('include_disabled', 'true');
		if (qs.toString()) url += `?${qs.toString()}`;
	}
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to fetch users'));
	return res.json();
}

export async function createAdminUser(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload || {}),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to create user'));
	return res.json();
}

export async function updateAdminUser(userId, payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/admin/users/${userId}`,
		{
			method: 'PATCH',
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
			body: JSON.stringify(payload || {}),
		}
	);
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to update user'));
	return res.json();
}

export async function deleteAdminUser(userId, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { force = false } = options || {};
	const qs = new URLSearchParams();
	if (force) qs.set('force', 'true');
	const url = `${API_BASE_URL}/api/admin/users/${userId}${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to delete user'));
	return res.json();
}

export async function fetchAuditLogs(filters = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');

	const qs = new URLSearchParams();
	if (filters.since) qs.set('since', String(filters.since));
	if (filters.until) qs.set('until', String(filters.until));
	if (filters.action) qs.set('action', String(filters.action));
	if (filters.actor) qs.set('actor', String(filters.actor));
	if (filters.entity_type) qs.set('entity_type', String(filters.entity_type));
	if (filters.entity_id != null && String(filters.entity_id).trim() !== '') qs.set('entity_id', String(filters.entity_id));
	if (filters.limit != null) qs.set('limit', String(filters.limit));
	if (filters.offset != null) qs.set('offset', String(filters.offset));

	const url = `${API_BASE_URL}/api/admin/audit${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to fetch audit log'));
	return res.json();
}

export async function fetchProjectsList() {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/projects-lite`, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return [];
	if (!res.ok) throw new Error('Failed to fetch projects');
	const rows = await res.json();
	const toYmd = (v) => {
		if (!v) return null;
		const d = new Date(v);
		if (isNaN(d.getTime())) return null;
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${dd}`;
	};
	return (Array.isArray(rows) ? rows : []).map(r => ({
		id: r.id,
		name: r.name || '—',
		client_name: r.client_name || '—',
		client_id: r.client_id ?? null,
		client_color: r.client_color || null,
		start_date: toYmd(r.start_date),
		end_date: toYmd(r.end_date),
		created_by_user_id: r.created_by_user_id || null,
		manager_user_id: r.manager_user_id || null,
		manager_id: r.manager_id || null,
		manager_display_name: r.manager_display_name || null,
		manager_name: r.manager_name || null,
		manager_first_name: r.manager_first_name || null,
		manager_last_name: r.manager_last_name || null,
		manager_email: r.manager_email || null,
		has_top_priority: !!r.has_top_priority,
	}));
}

export async function fetchClientsList(options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { projectsFallback } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return [];

	// Fallback for older backends: derive clients from projects-lite
	if (res.status === 404) {
		const projects = Array.isArray(projectsFallback) ? projectsFallback : await fetchProjectsList();
		const byId = new Map();
		for (const p of (Array.isArray(projects) ? projects : [])) {
			const cid = p?.client_id;
			if (cid == null) continue;
			const key = String(cid);
			if (byId.has(key)) continue;
			byId.set(key, { id: cid, name: p?.client_name || '—', color: p?.client_color || null });
		}
		return Array.from(byId.values()).sort((a, b) => String(a.name).localeCompare(String(b.name)));
	}

	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to fetch clients'));
	const rows = await res.json();
	return (Array.isArray(rows) ? rows : []).map(c => ({
		id: c.id,
		name: c.name || '—',
		color: c.color || null,
	})).sort((a, b) => String(a.name).localeCompare(String(b.name)));
}

export async function updateProject(projectId, payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/projects/${projectId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to update project'));
	return res.json();
}

export async function createProject(payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/projects`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error(await parseJsonErrorMessage(res, 'Failed to create project'));
	return res.json();
}


export async function fetchProjectOverview(projectId, range = 'this_week') {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams();
	if (range != null && String(range).trim() !== '') params.set('range', String(range));
	const qs = params.toString();
	const url = `${API_BASE_URL}/api/manager/projects/${projectId}/overview${qs ? `?${qs}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch project overview');
	return res.json();
}

export async function fetchProjectWorkItems(projectId, from, to) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams({ from, to });
	const url = `${API_BASE_URL}/api/manager/projects/${projectId}/work-items?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch project work items');
	return res.json();
}

export async function fetchProjectLoggedOther(projectId, from, to) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams({ from, to });
	const url = `${API_BASE_URL}/api/manager/projects/${projectId}/logged-other?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch other logged entries');
	return res.json();
}

export async function fetchProjectJobs(projectId, options = {}) {
	const { redirectOn401 = true } = options || {};
	try {
		const res = await fetchWithAuth(`${API_BASE_URL}/api/manager/projects/${projectId}/jobs`, { redirectOn401 });
		return res.json();
	} catch (err) {
		const status = err?.status;
		if (status === 401 || status === 403) {
			console.warn('fetchProjectJobs auth error:', err?.message || err);
			return [];
		}
		console.error('fetchProjectJobs error:', err);
		return [];
	}
}

export async function fetchJobTasks(jobId, options = {}) {
	const { redirectOn401 = true } = options || {};
	try {
		const res = await fetchWithAuth(`${API_BASE_URL}/api/manager/jobs/${jobId}/tasks`, { redirectOn401 });
		return res.json();
	} catch (err) {
		const status = err?.status;
		if (status === 401 || status === 403) {
			console.warn('fetchJobTasks auth error:', err?.message || err);
			return [];
		}
		console.error('fetchJobTasks error:', err);
		return [];
	}
}

export async function createProjectAssignment(projectId, payload, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/manager/projects/${projectId}/assignments`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload),
	});
	if (redirectOn401 && handleAuthRedirect(res)) return null;
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) {
		let msg = 'Failed to create assignment';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// --- Feedback / internal chat (manager/admin) ---
export async function fetchFeedbackMessages(params = {}, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const qs = new URLSearchParams();
	if (params && params.limit != null && params.limit !== '') qs.set('limit', String(params.limit));
	if (params && params.before != null && params.before !== '') qs.set('before', String(params.before));
	const url = `${API_BASE_URL}/api/feedback/messages${qs.toString() ? `?${qs.toString()}` : ''}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (redirectOn401 && handleAuthRedirect(res)) return { messages: [], has_more: false, oldest_id: null };
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) {
		let msg = 'Failed to fetch feedback messages';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function createFeedbackMessage(payload, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/feedback/messages`, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload || {}),
	});
	if (redirectOn401 && handleAuthRedirect(res)) return null;
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) {
		let msg = 'Failed to send message';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function updateFeedbackMessage(messageId, payload, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/feedback/messages/${messageId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload || {}),
	});
	if (redirectOn401 && handleAuthRedirect(res)) return null;
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) {
		let msg = 'Failed to update message';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function deleteFeedbackMessage(messageId, options = {}) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const { redirectOn401 = true } = options || {};
	const res = await fetch(`${API_BASE_URL}/api/feedback/messages/${messageId}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (redirectOn401 && handleAuthRedirect(res)) return null;
	if (res.status === 401) throw new Error('Not authenticated');
	if (!res.ok) {
		let msg = 'Failed to delete message';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Calendar feed (projects + assignments)
export async function fetchCalendar(from, to, projectId = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams({ from, to });
	if (projectId != null && projectId !== '') params.set('project_id', String(projectId));
	const url = `${API_BASE_URL}/api/calendar?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch calendar');
	const data = await res.json();
	// Normalize dates to YYYY-MM-DD strings for client rendering
	const toYmd = (v) => {
		if (!v) return null;
		const d = (v instanceof Date) ? v : new Date(v);
		if (isNaN(d.getTime())) return null;
		const y = d.getFullYear();
		const m = String(d.getMonth() + 1).padStart(2, '0');
		const dd = String(d.getDate()).padStart(2, '0');
		return `${y}-${m}-${dd}`;
	};
	const projects = (Array.isArray(data.projects) ? data.projects : []).map(p => ({
		id: p.id,
		name: p.name,
		client_name: p.client_name || '—',
		client_id: p.client_id ?? null,
		client_color: p.client_color ?? null,
		start_date: toYmd(p.start_date),
		end_date: toYmd(p.end_date),
		created_by_user_id: p.created_by_user_id || null,
	}));
	const assignments = (Array.isArray(data.assignments) ? data.assignments : []).map(a => ({
		id: a.id,
		project_id: a.project_id,
		project_name: a.project_name,
		created_by_user_id: a.created_by_user_id ?? null,
		created_by_name: a.created_by_name ?? null,
		client_name: a.client_name || '—',
		client_id: a.client_id ?? null,
		client_color: a.client_color ?? null,
		job_id: a.job_id,
		job_name: a.job_name,
		task_id: a.task_id || null,
		task_name: a.task_name || null,
		start_date: toYmd(a.start_date),
		due_date: toYmd(a.due_date),
		status: a.status,
		planned_hours: Number(a.planned_hours || 0),
		split_mode: a.split_mode || null,
		distribution_mode: a.distribution_mode || 'spread',
		include_weekends: a.include_weekends != null ? !!a.include_weekends : null,
		notes: a.notes || null,
		planned_minutes_total: a.planned_minutes_total != null ? Number(a.planned_minutes_total || 0) : null,
		daily_plan: Array.isArray(a.daily_plan)
			? a.daily_plan.map(r => ({ date: toYmd(r?.date), minutes: Number(r?.minutes || 0) })).filter(r => !!r.date)
			: null,
		top_priority: !!a.top_priority,
		assignees: Array.isArray(a.assignees)
			? a.assignees.map(u => ({
				user_id: u.user_id,
				display_name: u.display_name,
				accepted_at: u.accepted_at || null,
				planned_minutes: u.planned_minutes != null ? Number(u.planned_minutes || 0) : null,
				logged_minutes: u.logged_minutes != null ? Number(u.logged_minutes || 0) : null,
				progress: u.progress != null ? Number(u.progress || 0) : null,
				daily_plan: Array.isArray(u.daily_plan)
					? u.daily_plan.map(r => ({ date: toYmd(r?.date), minutes: Number(r?.minutes || 0) })).filter(r => !!r.date)
					: null,
			}))
			: [],
	}));
	return { from: toYmd(data.from), to: toYmd(data.to), projects, assignments };
}

export async function fetchCalendarPeople(from, to, userIds, options = {}) {
	const token = localStorage.getItem('token');
	const url = new URL(`${API_BASE_URL}/api/manager/calendar-people`);
	url.searchParams.set('from', from);
	url.searchParams.set('to', to);
	if (userIds && userIds.length) url.searchParams.set('user_ids', userIds.join(','));
	if (options && options.projectId != null && options.projectId !== '') url.searchParams.set('project_id', String(options.projectId));
	if (options && options.managerFilter && options.managerFilter !== 'all') url.searchParams.set('manager_filter', String(options.managerFilter));
	const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
	if (!res.ok) throw new Error('Failed to load calendar people');
	const j = await res.json();
	return j;
}

// Day details for calendar overlay
export async function fetchDayDetails(userId, date, optionsOrProjectId = null, maybeOptions = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	let options = {};
	if (optionsOrProjectId && typeof optionsOrProjectId === 'object' && !Array.isArray(optionsOrProjectId)) {
		options = optionsOrProjectId;
	} else {
		options = { projectId: optionsOrProjectId };
		if (maybeOptions && typeof maybeOptions === 'object' && !Array.isArray(maybeOptions)) {
			options = { ...options, ...maybeOptions };
		}
	}

	const params = new URLSearchParams({ date, user_id: String(userId) });
	if (options.projectId != null && options.projectId !== '') params.set('project_id', String(options.projectId));
	if (options.managerFilter && options.managerFilter !== 'all') params.set('manager_filter', String(options.managerFilter));

	const url = `${API_BASE_URL}/api/manager/day-details?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to load day details');
	return res.json();
}

// Me profile with theme preference
export async function fetchMe() {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me`, { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to load profile');
	const data = await res.json();
	return data?.user || null;
}

export async function updateMyTheme(theme_preference) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me/theme`, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify({ theme_preference })
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to update theme';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Persist UI theme per-user (new model)
export async function updateMyUiTheme(ui_theme) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me`, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify({ ui_theme })
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to update theme';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function changeMyPassword(new_password, confirm_password) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me/password`, {
		method: 'PATCH',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify({ new_password, confirm_password }),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to change password';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Settings endpoints
export async function fetchMySettings() {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me/settings`, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to load settings');
	return res.json();
}

export async function updateMySettings(theme) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/me/settings`, {
		method: 'PUT',
		headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
		body: JSON.stringify({ theme }),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to update settings';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// Assignment details
export async function fetchAssignment(assignmentId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}`, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to load assignment';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	const data = await res.json();
	// Normalize dates
	const toYmd = (v) => {
		if (!v) return null;
		const d = (v instanceof Date) ? v : new Date(v);
		if (isNaN(d.getTime())) return null;
		const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2,'0'); const dd = String(d.getDate()).padStart(2,'0');
		return `${y}-${m}-${dd}`;
	};
	return {
		id: data.id,
		project_id: data.project_id,
		project_name: data.project_name,
		client_name: data.client_name || '—',
		job_id: data.job_id,
		job_name: data.job_name,
		task_id: data.task_id || null,
		task_name: data.task_name || null,
		created_by_manager_id: data.created_by_manager_id || null,
		visibility_manager_user_id: data.visibility_manager_user_id || null,
		start_date: toYmd(data.start_date),
		due_date: toYmd(data.due_date),
		status: data.status,
		planned_hours: Number(data.planned_hours || 0),
		split_mode: data.split_mode || null,
		distribution_mode: data.distribution_mode || 'spread',
		include_weekends: !!data.include_weekends,
		notes: data.notes || '',
		assignees: Array.isArray(data.assignees) ? data.assignees.map(a => ({
			user_id: a.user_id,
			display_name: a.display_name,
			role: a.role || null,
			planned_hours_split: a.planned_hours_split,
			accepted_at: a.accepted_at || null,
			planned_minutes: Number(a.planned_minutes || 0),
			logged_minutes: Number(a.logged_minutes || 0),
			progress: Number(a.progress || 0),
		})) : [],
	};
}

export async function updateAssignment(assignmentId, payload) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}`, {
		method: 'PATCH',
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
		body: JSON.stringify(payload),
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to update assignment';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

export async function deleteAssignment(assignmentId) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(`${API_BASE_URL}/api/assignments/${assignmentId}`, {
		method: 'DELETE',
		headers: { Authorization: `Bearer ${token}` },
	});
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) {
		let msg = 'Failed to delete assignment';
		try { const d = await res.json(); if (d?.error) msg = d.error; } catch {}
		throw new Error(msg);
	}
	return res.json();
}

// --- Reports summaries (new) ---
function normalizePersonFilterId(value) {
	if (value == null) return null;
	const s = String(value).trim();
	if (!s) return null;
	const lower = s.toLowerCase();
	if (lower === 'all' || lower === 'null' || lower === 'undefined') return null;
	return s;
}

export async function fetchManagerReportSummary(from, to, projectId = null, personId = null, managerId = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams({ from, to });
	if (projectId != null && projectId !== '') params.set('project_id', String(projectId));
	const normalizedPersonId = normalizePersonFilterId(personId);
	if (normalizedPersonId) params.set('user_id', String(normalizedPersonId));
	if (managerId != null && String(managerId).trim() !== '') params.set('manager_id', String(managerId).trim());
	const url = `${API_BASE_URL}/api/reports/manager/summary?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch report');
	return res.json();
}

export async function fetchMyReportSummary(from, to, projectId = null) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const params = new URLSearchParams({ from, to });
	if (projectId != null && projectId !== '') params.set('project_id', String(projectId));
	const url = `${API_BASE_URL}/api/reports/me/summary?${params.toString()}`;
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return null;
	if (!res.ok) throw new Error('Failed to fetch report');
	return res.json();
}

// --- Exports (XLSX) ---
async function downloadBlob(url, filename) {
	const token = getToken();
	if (!token) throw new Error('Not authenticated');
	const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
	if (handleAuthRedirect(res)) return;
	if (!res.ok) throw new Error('Export failed');
	const blob = await res.blob();
	const href = URL.createObjectURL(blob);
	const a = document.createElement('a');
	a.href = href; a.download = filename; document.body.appendChild(a); a.click(); a.remove();
	URL.revokeObjectURL(href);
}

export async function exportMonthlyXlsx(month, mode = 'split') {
	const url = `${API_BASE_URL}/api/exports/monthly?month=${encodeURIComponent(month)}&mode=${encodeURIComponent(mode)}`;
	await downloadBlob(url, `monthly-${month}-${mode}.xlsx`);
}

export async function exportMonthlyPayrollAllocationXlsx(month) {
	const url = `${API_BASE_URL}/api/exports/monthly-payroll-allocation?month=${encodeURIComponent(month)}`;
	await downloadBlob(url, `monthly-payroll-allocation-${month}.xlsx`);
}

export async function exportDailyXlsx(from, to, mode = 'split') {
	const url = `${API_BASE_URL}/api/exports/daily?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&mode=${encodeURIComponent(mode)}`;
	await downloadBlob(url, `daily-${from}_to_${to}-${mode}.xlsx`);
}

// (dedupe) keep single definitions above

// Unified reports summary (normalize manager/me shapes)
export async function fetchReportsSummary({ from, to, projectId = null, managerId = null, role = 'employee', personId = null }) {
	// Guard: treat falsy/"all" personId as All people (omit user_id)
	const normalizedPersonId = normalizePersonFilterId(personId);
	const raw = role === 'manager'
		? await fetchManagerReportSummary(from, to, projectId, normalizedPersonId, managerId)
		: await fetchMyReportSummary(from, to, projectId);
	const byDay = Array.isArray(raw?.byDay) ? raw.byDay : Array.isArray(raw?.by_day) ? raw.by_day : [];
	const byProject = Array.isArray(raw?.byProject) ? raw.byProject : Array.isArray(raw?.by_project) ? raw.by_project : [];
	const byPerson = Array.isArray(raw?.byUser) ? raw.byUser : Array.isArray(raw?.by_person) ? raw.by_person : [];
	const totals = raw?.totals || raw?.kpis || {};
	const planned = Number(totals.planned_minutes || 0);
	const loggedAssigned = Number(totals.logged_assigned_minutes || 0);
	const loggedOther = Number(totals.logged_other_minutes || 0);
	const loggedTotal = Number(totals.logged_total_minutes || (loggedAssigned + loggedOther));
	const utilization_pct = planned > 0 ? Math.round((loggedTotal * 10000) / planned) / 100 : 0;
	const overbooked_days = byDay.filter(d => Number(d.logged_total_minutes || 0) > 8 * 60).length;
	return {
		kpis: { planned_minutes: planned, logged_assigned_minutes: loggedAssigned, logged_other_minutes: loggedOther, logged_total_minutes: loggedTotal, utilization_pct, overbooked_days },
		byDay,
		byProject,
		byPerson,
	};
}
